import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import * as React from "react";
import { format, startOfDay } from "date-fns";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  buildPresetRange,
  DEFAULT_PERIOD_PRESETS,
  matchPreset,
  PERIOD_PRESET_LABELS,
  type PeriodPreset,
} from "@/utils/period-presets";

interface PeriodPickerProps {
  value?: DateRange;
  /** Só é chamado com o intervalo completo — nunca com metade dele. */
  onChange: (range: { from: Date; to: Date }) => void;
  presets?: PeriodPreset[];
  /** Filtros de métricas não têm dados no futuro; formulários podem liberar. */
  disableFuture?: boolean;
  disabled?: boolean;
  placeholder?: string;
  align?: "start" | "center" | "end";
  className?: string;
}

const formatDay = (date: Date) => format(date, "dd/MM/yyyy");

/**
 * Um único controle para o período, portado da StudioSeller: os atalhos
 * resolvem o caso comum em um clique e o calendário atende quem precisa de um
 * intervalo exato.
 */
export function PeriodPicker({
  value,
  onChange,
  presets = DEFAULT_PERIOD_PRESETS,
  disableFuture = true,
  disabled,
  placeholder = translateInterfaceText("Selecionar período"),
  align = "start",
  className,
}: PeriodPickerProps) {
  useLanguage();
  const [open, setOpen] = React.useState(false);
  /**
   * Início de um intervalo em construção. Os dois cliques são controlados aqui
   * de propósito: o calendário, ao receber um intervalo já completo, ajustaria
   * um dos limites em vez de começar outro — e um clique só nunca deve valer
   * como intervalo escolhido.
   */
  const [pendingStart, setPendingStart] = React.useState<Date | null>(null);

  const today = startOfDay(new Date());
  const activePreset = matchPreset(value, presets, today);

  const selectedRange: DateRange | undefined = pendingStart
    ? { from: pendingStart, to: undefined }
    : value?.from && value?.to
      ? { from: value.from, to: value.to }
      : undefined;

  const triggerLabel = activePreset
    ? PERIOD_PRESET_LABELS[activePreset]
    : value?.from && value?.to
      ? `${formatDay(value.from)} – ${formatDay(value.to)}`
      : placeholder;

  function apply(range: { from: Date; to: Date }) {
    onChange(range);
    setPendingStart(null);
    setOpen(false);
  }

  function selectDay(day: Date) {
    const clicked = startOfDay(day);
    if (!pendingStart) {
      setPendingStart(clicked);
      return;
    }
    // O calendário permite clicar do fim para o começo.
    apply(
      pendingStart <= clicked
        ? { from: pendingStart, to: clicked }
        : { from: clicked, to: pendingStart }
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Fechar no meio da escolha descarta o intervalo pela metade.
        if (!next) setPendingStart(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            // Aqui `accent` é o azul da marca; o hover do gatilho fica neutro.
            "w-full justify-start gap-2.5 rounded-xl px-3.5 font-medium hover:bg-muted hover:text-foreground sm:w-auto",
            !value?.from && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="size-4 text-muted-foreground" aria-hidden="true" />
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="ml-auto size-4 text-muted-foreground sm:ml-1" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto max-w-[calc(100vw-2rem)] rounded-xl p-0">
        <div className="flex flex-col sm:flex-row">
          <div className="flex flex-col gap-1 border-b border-border p-2 sm:min-w-44 sm:border-b-0 sm:border-r sm:p-3">
            <p className="px-2 pb-2 pt-1 text-xs font-medium text-muted-foreground">{translateInterfaceText("Atalhos")}</p>
            {presets.map((preset) => {
              const isActive = !pendingStart && activePreset === preset;

              return (
                <Button
                  key={preset}
                  type="button"
                  variant="ghost"
                  aria-pressed={isActive}
                  onClick={() => apply(buildPresetRange(preset, today))}
                  className={cn(
                    "justify-start rounded-xl font-normal hover:bg-muted hover:text-foreground",
                    isActive && "bg-muted font-medium text-foreground"
                  )}
                >
                  {PERIOD_PRESET_LABELS[preset]}
                </Button>
              );
            })}
          </div>

          <div className="p-2 sm:p-3">
            <Calendar
              mode="range"
              aria-label={translateInterfaceText("Escolher intervalo")}
              selected={selectedRange}
              defaultMonth={selectedRange?.to ?? selectedRange?.from ?? today}
              disabled={disableFuture ? { after: today } : undefined}
              onSelect={() => {
                // A seleção é derivada dos cliques em `selectDay`.
              }}
              onDayClick={(day, modifiers) => {
                if (modifiers.disabled) return;
                selectDay(day);
              }}
            />
            <p aria-live="polite" className="min-h-5 px-3 pb-1 text-xs text-muted-foreground">
              {pendingStart ? translateInterfaceText("Agora escolha o fim do intervalo") : null}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
